import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, eur, fmtDate, fmtDateTime, openBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageTitle, StatusBadge, Chip, Field, SearchBox, matchQ, focusRing } from "../components/common";
import { useFocusParam, useScrollTo } from "../hooks/useFocusParam";
import { Modal, SideCalc, ComponentsView } from "../components/shared";
import { DateRange, monthStart, today, rangeParams } from "../components/DateRange";

function Detail({ id, onClose, user, focusPid }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get(`/declarations/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  const ask = async () => {
    try {
      await api.post(`/declarations/${id}/correction-request`, { message: msg });
      toast.success("Demande de correction envoyée");
      setMsg("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return null;
  const sub = user.role !== "chauffeur";
  return (
    <Modal title={d.ref} onClose={onClose} testid="declaration-detail">
      <div className="text-sm text-slate-600 mb-3">{fmtDate(d.work_date)} · {d.driver_name} · {d.company_name} · envoyée le {fmtDateTime(d.submitted_at)}</div>
      <div className="space-y-3">
        {d.prestations.map((p) => (
          <div key={p.id} data-focus-id={p.id} className={`tl-card space-y-2 ${focusRing(focusPid === p.id)} ${p.components.some((c) => c.type === "pm") ? "!border-red-400 bg-red-50 text-red-800" : ""}`} data-testid={`prestation-${p.ref}`}>
            <div className="flex gap-2 items-center flex-wrap"><span className="tl-mono font-semibold">{p.ref}</span><StatusBadge status={p.control_status} />{p.mixed && <Chip tone="amber">mixte</Chip>}</div>
            <ComponentsView p={p} />
            {sub && p.calc?.sub && <SideCalc calc={p.calc.sub} label="Montant sous-traitant" testid={`sub-calc-${p.ref}`} />}
            {p.calc?.ups && <SideCalc calc={p.calc.ups} label="Montant UPS" testid={`ups-calc-${p.ref}`} />}
          </div>
        ))}
        {d.files?.length > 0 && <div className="flex flex-wrap gap-2">{d.files.map((f) => <button key={f.id} className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => openBlob(`/files/${f.id}/download`)}>{f.filename}</button>)}</div>}
        {user.role !== "admin" && user.role !== "gestionnaire" && (
          <div className="space-y-2">
            <Field label="Signaler un désaccord / demander une correction"><textarea className="tl-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="correction-request-input" /></Field>
            <button className="tl-btn tl-btn-dark" disabled={!msg.trim()} onClick={ask} data-testid="correction-request-submit">Envoyer la demande</button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function Prestations() {
  const { user } = useAuth();
  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const focus = useFocusParam(["did", "pid"]);
  useEffect(() => {
    if (focus.did) setOpen(focus.did);
  }, [focus.did, focus.pid]);
  const shown = rows.filter((d) => matchQ(q, d.ref, d.driver_name, d.company_name, d.employee_id, d.route_id, d.prestations.map((p) => p.ref).join(" ")));
  useScrollTo(focus.did, shown.length);
  useEffect(() => {
    api.get("/declarations", { params: rangeParams(range) }).then((r) => setRows(r.data));
  }, [range]);
  const showSub = user.role !== "chauffeur";
  return (
    <div>
      <PageTitle title="Mes prestations" sub="Déclarations enregistrées, état du contrôle et intégration Excel.">
        <SearchBox value={q} onChange={setQ} testid="prestations-search" />
        <DateRange value={range} onChange={setRange} testid="prestations-range" />
      </PageTitle>
      {rows.length === 0 && <div className="tl-card text-slate-500" data-testid="empty-prestations">Aucune déclaration pour cette période.</div>}
      <div className="grid gap-3 md:grid-cols-2">
        {shown.map((d) => (
          <button key={d.id} data-focus-id={d.id} onClick={() => setOpen(d.id)} className={`tl-card text-left hover:border-amber-500 transition-colors tl-fade ${focusRing(focus.did === d.id)}`} data-testid={`declaration-card-${d.ref}`}>
            <div className="flex items-center gap-2"><span className="tl-mono font-semibold">{d.ref}</span>
              <Chip tone="green">Reçue</Chip>
              <Chip tone={d.excel_status === "integree" ? "blue" : "slate"}>{d.excel_status === "integree" ? "Intégrée dans Excel" : "Non intégrée"}</Chip>
            </div>
            <div className="mt-1 font-semibold">{fmtDate(d.work_date)} · {d.driver_name}</div>
            <div className="text-xs text-slate-500">{d.company_name}</div>
            <div className="flex flex-wrap gap-1 mt-2">{d.prestations.map((p) => <StatusBadge key={p.id} status={p.control_status} testid={`badge-${p.ref}`} />)}</div>
            {showSub && <div className="mt-2 text-sm">Sous-traitant : <b className="tl-mono">{eur(d.prestations.reduce((s, p) => s + (p.calc?.sub?.known_total || 0), 0))}</b>
              {d.prestations.some((p) => p.calc?.sub?.status !== "complet") && <span className="text-amber-700"> · à confirmer</span>}</div>}
            {d.flags?.length > 0 && <div className="mt-1 text-xs text-red-700">{d.flags.join(" · ")}</div>}
          </button>
        ))}
      </div>
      {open && <Detail id={open} user={user} focusPid={focus.pid} onClose={() => setOpen(null)} />}
    </div>
  );
}
